import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  OutlinedInput,
  ListItemText,
  CircularProgress,
  FormHelperText,
  Divider,
  Typography,
  Box,
  Switch,
  List,
  ListItem,
  Chip,
  Alert,
  IconButton,
  ListItemSecondaryAction,
  Autocomplete,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { Restore, Star, StarBorder, Add } from '@mui/icons-material'
import { Link } from 'react-router-dom'
import SnackbarMessage from '../SnackbarMessage'
import { useAuth } from '../../context/AuthContext'
import supabase from '../../supabaseClient'

function EditStudentForm({ student, open, onClose, onSuccess }) {
  const { userRole } = useAuth()
  const isAdmin = userRole === 'admin' || userRole === 'super_admin'
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    class_ids: [],
    is_quran_student: false,
    is_greenbook_student: false,
    quran_completed: false,
    is_self_guardian: false
  })
  const [guardians, setGuardians] = useState([])
  const [availableGuardians, setAvailableGuardians] = useState([])
  const [addGuardianDialogOpen, setAddGuardianDialogOpen] = useState(false)
  const [selectedGuardian, setSelectedGuardian] = useState(null)
  const [relationshipType, setRelationshipType] = useState('')
  const [otherRelationType, setOtherRelationType] = useState('')
  const [age, setAge] = useState(0)
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [guardianCount, setGuardianCount] = useState(0)
  const [pendingGuardianChanges, setPendingGuardianChanges] = useState({
    deletions: [],
    primaryId: null,
    additions: [] // Array of { guardian_id, relationship_type }
  })
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })

  console.log(formData)


  // Reset form when dialog opens with student data
  useEffect(() => {
    if (open && student) {
      setFormData({
        first_name: student.first_name || '',
        last_name: student.last_name || '',
        date_of_birth: student.date_of_birth || '',
        gender: student.gender || '',
        class_ids: student.class_ids || [],
        is_quran_student: student.is_quran_student || false,
        is_greenbook_student: student.is_greenbook_student || false,
        quran_completed: student.quran_completed || false,
        is_self_guardian: student.is_self_guardian || false,
      });

      // Calculate age if date of birth is available
      if (student.date_of_birth) {
        const birthDate = new Date(student.date_of_birth);
        const today = new Date();
        const newAge = today.getFullYear() - birthDate.getFullYear();
        setAge(newAge);

        // If student is under 18, ensure they can't be self guardian
        if (newAge < 18 && student.is_self_guardian) {
          setFormData(prev => ({
            ...prev,
            is_self_guardian: false
          }));
        }
      }

      // Fetch guardians for this student
      fetchStudentGuardians(student.id);
    }
  }, [student, open])

  useEffect(() => {
    const fetchClasses = async () => {
      const { data, error } = await supabase.from('classes').select('id, name')
      if (!error) setClasses(data || [])
    }
    fetchClasses()
  }, [])

  // Validate when age or guardian count changes
  useEffect(() => {
    if (age < 18 && guardianCount === 0) {
      setError('At least one guardian is required for students under 18')
    } else {
      setError('')
    }
  }, [age, guardianCount])

  // If not admin, don't render anything
  if (!isAdmin) return null

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    if (name === 'first_name' || name === 'last_name') {
      // Only allow letters and spaces
      const letterOnlyValue = value.replace(/[^A-Za-z\s]/g, '')
      setFormData((prev) => ({
        ...prev,
        [name]: letterOnlyValue,
      }))
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }))
    }
  }


  const handleClassChange = (e) => {
    const { value } = e.target
    setFormData((prev) => ({
      ...prev,
      class_ids: typeof value === 'string' ? value.split(',') : value,
    }))
  }

  const fetchStudentGuardians = async (studentId) => {
    try {
      const { data, error } = await supabase
        .from('student_guardians')
        .select(`
          *,
          guardians (*)
        `)
        .eq('student_id', studentId)
        .order('is_primary', { ascending: false });

      if (error) throw error;
      setGuardians(data || []);
      setGuardianCount(data?.length || 0);
    } catch (error) {
      console.error('Error fetching student guardians:', error);
      setError('Failed to fetch guardians');
    }
  };


  const handleRemoveGuardian = (studentGuardianId) => {
    // Find if this is the primary guardian
    const isRemovingPrimary = guardians.find(g => g.id === studentGuardianId)?.is_primary;
    
    setPendingGuardianChanges(prev => {
      const newDeletions = [...prev.deletions, studentGuardianId];
      let newPrimaryId = prev.primaryId;

      // If we're deleting the current or pending primary guardian
      if (isRemovingPrimary || prev.primaryId === studentGuardianId) {
        // Find the first non-deleted guardian to make primary
        const remainingGuardian = guardians.find(g => 
          g.id !== studentGuardianId && 
          !newDeletions.includes(g.id)
        );
        newPrimaryId = remainingGuardian ? remainingGuardian.id : null;
      }

      return {
        ...prev,
        deletions: newDeletions,
        primaryId: newPrimaryId
      };
    });
    
    // Update local guardian count for validation
    setGuardianCount(prev => prev - 1);
  };

  const handleSetPrimary = (studentGuardianId) => {
    setPendingGuardianChanges(prev => ({
      ...prev,
      primaryId: studentGuardianId
    }));
  };

  const fetchAvailableGuardians = async () => {
    try {
      const { data, error } = await supabase
        .from('guardians')
        .select('*')
        .order('first_name');

      if (error) throw error;

      // Filter out already assigned guardians
      const assignedIds = guardians.map(sg => sg.guardian_id);
      const available = (data || []).filter(g => !assignedIds.includes(g.id));
      setAvailableGuardians(available);
    } catch (error) {
      console.error('Error fetching available guardians:', error);
      setError('Failed to fetch available guardians');
    }
  };

  const handleAddGuardian = () => {
    if (!selectedGuardian) return;

    const newGuardian = {
      guardian_id: selectedGuardian.id,
      relationship_type: relationshipType === 'Other' ? otherRelationType : relationshipType,
      guardian_data: selectedGuardian // Store the full guardian data for display
    };

    setPendingGuardianChanges(prev => {
      // If this is the first guardian (including pending ones), make it primary
      const shouldBePrimary = guardians.length === 0 && prev.additions.length === 0;
      
      return {
        ...prev,
        additions: [...prev.additions, newGuardian],
        primaryId: shouldBePrimary ? `pending_${selectedGuardian.id}` : prev.primaryId
      };
    });

    // Update guardian count for validation
    setGuardianCount(prev => prev + 1);

    // Close dialog and reset its state
    setAddGuardianDialogOpen(false);
    setSelectedGuardian(null);
    setRelationshipType('');
    setOtherRelationType('');
  };

  const handleOpenAddGuardianDialog = () => {
    fetchAvailableGuardians();
    setAddGuardianDialogOpen(true);
    setError('');
  };

  const handleSelfGuardianToggle = async () => {
    try {
      setLoading(true);
      
      // Safety check - prevent enabling self-guardian for underage students
      if (age < 18 && !formData.is_self_guardian) {
        setError('Student must be 18 or older to be self guardian');
        return;
      }

      // Safety check - prevent disabling self-guardian if there are no other guardians
      if (formData.is_self_guardian && guardians.length === 0) {
        setError('Cannot disable self-guardian status when no other guardians are assigned');
        return;
      }

      setFormData(prev => ({
        ...prev,
        is_self_guardian: !prev.is_self_guardian
      }));
      
    } catch (error) {
      console.error('Error updating self guardian status:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false })
  }


  const handleClose = () => {
    // Reset form data to original student data
    if (student) {
      setFormData({
        first_name: student.first_name || '',
        last_name: student.last_name || '',
        date_of_birth: student.date_of_birth || '',
        gender: student.gender || '',
        class_ids: student.class_ids || [],
        is_quran_student: student.is_quran_student || false,
        is_greenbook_student: student.is_greenbook_student || false,
        quran_completed: student.quran_completed || false,
        is_self_guardian: student.is_self_guardian || false,
      })
    }
    
    // Reset guardian changes
    setPendingGuardianChanges({
      deletions: [],
      primaryId: null,
      additions: []
    })
    
    // Reset other form state
    setError('')
    setSelectedGuardian(null)
    setRelationshipType('')
    setOtherRelationType('')
    setAddGuardianDialogOpen(false)
    
    // Refresh guardians list and count to original state
    if (student) {
      fetchStudentGuardians(student.id).then(() => {
        // Reset guardian count to match the actual number of guardians
        setGuardianCount(guardians.length)
      })
    }
    
    onClose()
  }

  const hasValidationErrors = () => {
    // Check for empty required fields
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      return true
    }

    // Check for gender
    if (!formData.gender) {
      return true
    }

    // Require at least one guardian for students under 18
    if (age < 18 && guardianCount === 0) {
      return true
    }

    return false
  }

  const validateForm = () => {
    if (!formData.first_name.trim()) {
      setError('First name is required')
      return false
    }
    if (!formData.last_name.trim()) {
      setError('Last name is required')
      return false
    }
    if (!formData.gender) {
      setError('Gender is required')
      return false
    }
    if (age < 18 && guardianCount === 0) {
      setError('At least one guardian is required for students under 18')
      return false
    }
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (!validateForm()) {
      setLoading(false)
      return
    }

    try {
      // Start a Supabase transaction
      // First update student information
      const { error: updateError } = await supabase
        .from('students')
        .update({
          first_name: formData.first_name.trim() || null,
          last_name: formData.last_name.trim(),
          date_of_birth: formData.date_of_birth || null,
          gender: formData.gender || null,
          class_ids: formData.class_ids || null,
          is_quran_student: formData.is_quran_student || null,
          is_greenbook_student: formData.is_greenbook_student || null,
          quran_completed: formData.quran_completed || null,
          is_self_guardian: formData.is_self_guardian,
          updated_at: new Date().toISOString(),
        })
        .eq('id', student.id)

      if (updateError) throw updateError

      // Handle guardian deletions
      if (pendingGuardianChanges.deletions.length > 0) {
        const { error: deleteError } = await supabase
          .from('student_guardians')
          .delete()
          .in('id', pendingGuardianChanges.deletions)

        if (deleteError) throw deleteError
      }

      // Handle pending additions
      if (pendingGuardianChanges.additions.length > 0) {
        const additionsData = pendingGuardianChanges.additions.map(newGuardian => ({
          student_id: student.id,
          guardian_id: newGuardian.guardian_id,
          relationship_type: newGuardian.relationship_type,
          is_primary: pendingGuardianChanges.primaryId === `pending_${newGuardian.guardian_id}`
        }));

        const { error: addError } = await supabase
          .from('student_guardians')
          .insert(additionsData);

        if (addError) throw addError;
      }

      // Handle primary guardian change (only for existing guardians)
      if (pendingGuardianChanges.primaryId && !pendingGuardianChanges.primaryId.startsWith('pending_')) {
        // First, unset all as primary
        await supabase
          .from('student_guardians')
          .update({ is_primary: false })
          .eq('student_id', student.id)

        // Then set the new primary
        const { error: primaryError } = await supabase
          .from('student_guardians')
          .update({ is_primary: true })
          .eq('id', pendingGuardianChanges.primaryId)

        if (primaryError) throw primaryError
      }

      setSnackbar({
        open: true,
        message: 'Student information updated successfully!',
        severity: 'success',
      })

      // Reset pending changes
      setPendingGuardianChanges({
        deletions: [],
        primaryId: null,
        additions: []
      })

      // Refresh guardians list
      await fetchStudentGuardians(student.id)

      if (onSuccess) onSuccess()
      handleClose()
    } catch (error) {
      console.error('Error updating student:', error)
      setSnackbar({
        open: true,
        message: 'Failed to update student information. Please try again.',
        severity: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            maxHeight: { xs: '100vh', sm: '90vh' },
            margin: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Edit Student Information
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} sx={{ mt: { xs: 0.5, sm: 1 } }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  fullWidth
                  error={!formData.first_name.trim()}
                  helperText={!formData.first_name.trim() ? 'First name is required' : ''}
                  inputProps={{ pattern: '[A-Za-z\\s]*' }}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  fullWidth
                  error={!formData.last_name.trim()}
                  helperText={!formData.last_name.trim() ? 'Last name is required' : ''}
                  inputProps={{ pattern: '[A-Za-z\\s]*' }}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <TextField
                    label="Date of Birth"
                    name="date_of_birth"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => {
                      handleChange(e);
                      // Update age and self-guardian status
                      if (e.target.value) {
                        const birthDate = new Date(e.target.value);
                        const today = new Date();
                        const newAge = today.getFullYear() - birthDate.getFullYear();
                        setAge(newAge);
                      }
                    }}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required error={!formData.gender}>
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Gender</InputLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    label="Gender"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      '& .MuiSelect-select': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                    }}
                  >
                    <MenuItem value="Male" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Male</MenuItem>
                    <MenuItem value="Female" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Female</MenuItem>
                  </Select>
                  {!formData.gender && (
                    <FormHelperText sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Gender is required</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Class Assignments</InputLabel>
                  <Select
                    multiple
                    name="class_ids"
                    value={formData.class_ids}
                    onChange={handleClassChange}
                    input={<OutlinedInput label="Class Assignments" />}
                    renderValue={(selected) =>
                      classes
                        .filter((c) => selected.includes(c.id))
                        .map((c) => c.name)
                        .join(', ')
                    }
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      '& .MuiSelect-select': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                    }}
                  >
                    {classes.map((c) => (
                      <MenuItem key={c.id} value={c.id} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        <Checkbox checked={formData.class_ids.indexOf(c.id) > -1} size="small" />
                        <ListItemText primary={c.name} primaryTypographyProps={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} />
                      </MenuItem>
                    ))}
                  </Select>
                  {/* <FormHelperText>Select one or more classes</FormHelperText> */}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_quran_student}
                      onChange={handleChange}
                      name="is_quran_student"
                      size="small"
                    />
                  }
                  label="Quran Student"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, '& .MuiFormControlLabel-label': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_greenbook_student}
                      onChange={handleChange}
                      name="is_greenbook_student"
                      size="small"
                    />
                  }
                  label="Greenbook Student"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, '& .MuiFormControlLabel-label': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.quran_completed}
                      onChange={handleChange}
                      name="quran_completed"
                      size="small"
                    />
                  }
                  label="Quran Completed"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, '& .MuiFormControlLabel-label': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
                />
              </Grid>
            </Grid>
          </form>

          {/* Guardian Management Section */}
          <Divider sx={{ my: { xs: 2, sm: 3 } }} />
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            mb: 2,
            gap: { xs: 2, sm: 0 },
          }}>
            <Typography variant="h6" sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
              Guardian Management
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, sm: 2 }, alignItems: 'stretch', width: { xs: '100%', sm: 'auto' } }}>
              <Button
                startIcon={<Add sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                onClick={handleOpenAddGuardianDialog}
                variant="outlined"
                size="small"
                fullWidth={isMobile}
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  padding: { xs: '8px 16px', sm: '6px 16px' },
                }}
              >
                Add Guardian
              </Button>
              {age >= 18 && (
              <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_self_guardian}
                      onChange={handleSelfGuardianToggle}
                      disabled={loading || (formData.is_self_guardian && guardians.length === 0)}
                      size="small"
                    />
                  }
                  label="Self Guardian"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, '& .MuiFormControlLabel-label': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
                />
                {formData.is_self_guardian && guardians.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: { xs: 0, sm: 2 }, mt: { xs: 0.5, sm: 0 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Add a guardian before disabling
                  </Typography>
                )}
              </Box>
            )}
            </Box>
          </Box>

          {formData.is_self_guardian && age >= 18 && (
            <Alert severity="info" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' }, '& .MuiAlert-message': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}>
              This student is their own guardian. Additional guardians can still be added if needed.
            </Alert>
          )}
          
          {formData.is_self_guardian && age < 18 && (
            <Alert severity="warning" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' }, '& .MuiAlert-message': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}>
              Student must be 18 or older to be self guardian. This setting will be automatically disabled.
            </Alert>
          )}

          {(guardians.length > 0 || pendingGuardianChanges.additions.length > 0) ? (
            <List dense>
              {/* Show existing guardians */}
              {guardians.map((sg) => {
                const isPendingDeletion = pendingGuardianChanges.deletions.includes(sg.id);
                const isPendingPrimary = pendingGuardianChanges.primaryId === sg.id;
                const isCurrentPrimary = sg.is_primary && !pendingGuardianChanges.primaryId;

                return (
                  <ListItem 
                    key={sg.id} 
                    divider
                    sx={{
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: isPendingPrimary ? 'action.selected' : 'inherit',
                        opacity: isPendingDeletion ? 0.6 : 1,
                        pointerEvents: 'none'
                      }
                    }}
                  >
                    <ListItemText
                      primaryTypographyProps={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Link
                            to={`/parent_details/${sg.guardians.id}`}
                            style={{ 
                              color: 'var(--primary-light)', 
                              textDecoration: isPendingDeletion ? 'line-through' : 'none',
                              fontWeight: 'bold',
                              position: 'relative',
                              fontSize: isMobile ? '0.875rem' : '1rem',
                            }}
                          >
                            {`${sg.guardians.first_name} ${sg.guardians.last_name}`}
                          </Link>
                          {(isCurrentPrimary || isPendingPrimary) && !isPendingDeletion && (
                            <Chip 
                              label={isPendingPrimary ? "Pending Primary" : "Primary"} 
                              size="small" 
                              color={isPendingPrimary ? "warning" : "primary"}
                              sx={{ 
                                position: 'relative',
                                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                height: { xs: '20px', sm: '24px' },
                              }}
                            />
                          )}
                          {isPendingDeletion && (
                            <Chip 
                              label="Pending Deletion" 
                              size="small" 
                              color="error"
                              sx={{ 
                                position: 'relative',
                                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                height: { xs: '20px', sm: '24px' },
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography 
                          variant="body2" 
                          color="textSecondary"
                          sx={{ 
                            textDecoration: isPendingDeletion ? 'line-through' : 'none',
                            position: 'relative',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          }}
                        >
                          {sg.guardians.phone || 'No phone'} • {sg.guardians.email || 'No email'}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        onClick={() => handleSetPrimary(sg.id)}
                        disabled={isCurrentPrimary || isPendingPrimary || isPendingDeletion}
                        size="small"
                        title={
                          isPendingDeletion ? "Cannot set primary while pending deletion" :
                          isCurrentPrimary ? "Primary guardian" : 
                          "Set as primary"
                        }
                      >
                        {isCurrentPrimary || isPendingPrimary ? 
                          <Star color={isPendingDeletion ? "disabled" : isPendingPrimary ? "warning" : "primary"} /> : 
                          <StarBorder color={isPendingDeletion ? "disabled" : "inherit"} />
                        }
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          if (isPendingDeletion) {
                            setPendingGuardianChanges(prev => ({
                              ...prev,
                              deletions: prev.deletions.filter(id => id !== sg.id)
                            }));
                            setGuardianCount(prev => prev + 1);
                          } else {
                            handleRemoveGuardian(sg.id);
                          }
                        }
                        }
                        disabled={!isPendingDeletion && guardians.length - pendingGuardianChanges.deletions.length === 1 && !formData.is_self_guardian}
                        size="small"
                        title={isPendingDeletion ? 
                          "Restore guardian" : 
                          guardians.length - pendingGuardianChanges.deletions.length === 1 && !formData.is_self_guardian ? 
                            "Cannot remove last guardian unless student is self-guardian" : 
                            "Remove guardian"
                        }
                      >
                        <Restore sx={{ 
                          transform: isPendingDeletion ? 'none' : 'rotate(90deg)',
                          transition: 'transform 0.2s'
                        }} />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
              {/* Show pending additions */}
              {pendingGuardianChanges.additions.map((newGuardian) => {
                const isPendingPrimary = pendingGuardianChanges.primaryId === `pending_${newGuardian.guardian_id}`;
                
                return (
                  <ListItem 
                    key={`pending_${newGuardian.guardian_id}`} 
                    divider
                    sx={{
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: isPendingPrimary ? 'action.selected' : 'inherit',
                        opacity: 0.7,
                        pointerEvents: 'none'
                      }
                    }}
                  >
                    <ListItemText
                      primaryTypographyProps={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Link
                            to={`/parent_details/${newGuardian.guardian_id}`}
                            style={{ 
                              color: 'var(--primary-light)', 
                              fontWeight: 'bold',
                              position: 'relative',
                              fontSize: isMobile ? '0.875rem' : '1rem',
                            }}
                          >
                            {`${newGuardian.guardian_data.first_name} ${newGuardian.guardian_data.last_name}`}
                          </Link>
                          <Chip 
                            label="Pending Addition" 
                            size="small" 
                            color="warning"
                            sx={{ 
                              position: 'relative',
                              fontSize: { xs: '0.65rem', sm: '0.75rem' },
                              height: { xs: '20px', sm: '24px' },
                            }}
                          />
                          {isPendingPrimary && (
                            <Chip 
                              label="Will be Primary" 
                              size="small" 
                              color="warning"
                              sx={{ 
                                position: 'relative',
                                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                height: { xs: '20px', sm: '24px' },
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography 
                            variant="body2" 
                            color="textSecondary"
                            sx={{ position: 'relative', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                          >
                            {newGuardian.guardian_data.phone || 'No phone'} • {newGuardian.guardian_data.email || 'No email'}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="textSecondary"
                            sx={{ position: 'relative', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                          >
                            Relationship: {newGuardian.relationship_type}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        onClick={() => {
                          setPendingGuardianChanges(prev => ({
                            ...prev,
                            additions: prev.additions.filter(g => g.guardian_id !== newGuardian.guardian_id),
                            primaryId: prev.primaryId === `pending_${newGuardian.guardian_id}` ? null : prev.primaryId
                          }));
                          setGuardianCount(prev => prev - 1);
                        }}
                        size="small"
                        title="Cancel addition"
                      >
                        <Restore />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <Typography color="textSecondary" sx={{ fontStyle: 'italic', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              No guardians assigned to this student
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 1 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}>
          {(pendingGuardianChanges.deletions.length > 0 || 
            pendingGuardianChanges.additions.length > 0 || 
            pendingGuardianChanges.primaryId ||
            JSON.stringify(formData) !== JSON.stringify({
              first_name: student?.first_name || '',
              last_name: student?.last_name || '',
              date_of_birth: student?.date_of_birth || '',
              gender: student?.gender || '',
              class_ids: student?.class_ids || [],
              is_quran_student: student?.is_quran_student || false,
              is_greenbook_student: student?.is_greenbook_student || false,
              quran_completed: student?.quran_completed || false,
              is_self_guardian: student?.is_self_guardian || false,
            })
          ) && (
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                textAlign: 'center',
                fontStyle: 'italic',
                mb: { xs: 0, sm: 1 },
                fontSize: { xs: '0.875rem', sm: '1rem' },
              }}
            >
              Changes will be applied when the form is saved
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', width: { xs: '100%', sm: 'auto' }, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button 
              onClick={handleClose}
              fullWidth={isMobile}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                padding: { xs: '10px 16px', sm: '8px 16px' },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={loading || hasValidationErrors()}
              fullWidth={isMobile}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                padding: { xs: '10px 16px', sm: '8px 16px' },
              }}
            >
              {loading ? <CircularProgress size={isMobile ? 20 : 24} /> : 'Save Changes'}
            </Button>
          </Box>
          {error && (
            <Typography variant="caption" color="error" sx={{ mt: { xs: 0, sm: 0.5 }, textAlign: { xs: 'center', sm: 'right' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              {error}
            </Typography>
          )}
        </DialogActions>
      </Dialog>

      <SnackbarMessage
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />

      {/* Add Guardian Dialog */}
      <Dialog 
        open={addGuardianDialogOpen} 
        onClose={() => setAddGuardianDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            maxHeight: { xs: '100vh', sm: '90vh' },
            margin: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Add Guardian
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          {error && <Alert severity="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' }, '& .MuiAlert-message': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}>{error}</Alert>}
          
          <Autocomplete
            fullWidth
            options={availableGuardians}
            getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
            value={selectedGuardian}
            onChange={(event, newValue) => {
              setSelectedGuardian(newValue);
              if (newValue) {
                setRelationshipType(newValue.gender === 'Male' ? 'Father' : 'Mother');
              }
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Select Guardian" 
                margin="normal"
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box>
                  <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {`${option.first_name} ${option.last_name}`}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {option.phone || 'No phone'} • {option.email || 'No email'}
                  </Typography>
                </Box>
              </li>
            )}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Relationship Type</InputLabel>
            <Select
              value={relationshipType}
              onChange={(e) => setRelationshipType(e.target.value)}
              label="Relationship Type"
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '& .MuiSelect-select': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
              }}
            >
              <MenuItem value="Mother" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Mother</MenuItem>
              <MenuItem value="Father" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Father</MenuItem>
              <MenuItem value="Other" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Other</MenuItem>
            </Select>
          </FormControl>

          {relationshipType === 'Other' && (
            <FormControl fullWidth margin="normal">
              <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Other Relationship Type</InputLabel>
              <Select
                value={otherRelationType}
                onChange={(e) => setOtherRelationType(e.target.value)}
                label="Other Relationship Type"
                sx={{
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  '& .MuiSelect-select': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  },
                }}
              >
                <MenuItem value="Aunt" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Aunt</MenuItem>
                <MenuItem value="Uncle" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Uncle</MenuItem>
                <MenuItem value="Grandfather" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Grandfather</MenuItem>
                <MenuItem value="Grandmother" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Grandmother</MenuItem>
                <MenuItem value="Brother" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Brother</MenuItem>
                <MenuItem value="Sister" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Sister</MenuItem>
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 1 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}>
          <Button 
            onClick={() => setAddGuardianDialogOpen(false)}
            fullWidth={isMobile}
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' },
              padding: { xs: '10px 16px', sm: '8px 16px' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddGuardian}
            disabled={!selectedGuardian || loading}
            variant="contained"
            fullWidth={isMobile}
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' },
              padding: { xs: '10px 16px', sm: '8px 16px' },
            }}
          >
            {loading ? <CircularProgress size={isMobile ? 20 : 24} /> : 'Add Guardian'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default EditStudentForm